/**
 * "N new since you last read" (mobile top bar, 2026-10-25): when this reader last had the
 * conversation open, per container, in this browser's localStorage. Written when the panel opens
 * and again when it closes (what they saw while it was open, their own comment included), only
 * ever forward. No value = a first visit: the bar says nothing about new comments.
 */
import { PREVIEW_PAGE_SIZE } from './useConversationPreview'

export function conversationLastReadKey(containerId: string): string {
  return `conversation:last-read:${containerId}`
}

/** The last-read time (ms), or null: never read here, storage blocked, or a bad value. */
export function readConversationLastRead(containerId: string): number | null {
  try {
    const raw = window.localStorage.getItem(conversationLastReadKey(containerId))
    const at = raw === null ? NaN : Number(raw)
    return Number.isFinite(at) && at > 0 ? at : null
  } catch {
    return null
  }
}

/** Moves the last-read time forward to `at` (never back: another tab may be further on). */
export function markConversationRead(containerId: string, at: number = Date.now()): void {
  try {
    const current = readConversationLastRead(containerId)
    if (current === null || at > current) {
      window.localStorage.setItem(conversationLastReadKey(containerId), String(at))
    }
  } catch {
    // storage blocked: the bar never claims new comments
  }
}

/**
 * Live top-level comments newer than `lastRead`, from the preview's newest page; null on a first
 * visit. Capped at `PREVIEW_PAGE_SIZE` (the page holds no more — `formatNewSince` says "10+").
 */
export function newSinceLastRead(
  createdAt: readonly number[],
  lastRead: number | null
): number | null {
  if (lastRead === null) {
    return null
  }
  return Math.min(createdAt.filter(at => at > lastRead).length, PREVIEW_PAGE_SIZE)
}

/** "3 new" / "10+ new" — the bar's inline count (`formatNewSince` is its full title). */
export function formatNewShort(n: number): string {
  return `${n >= PREVIEW_PAGE_SIZE ? `${PREVIEW_PAGE_SIZE}+` : n} new`
}

/** "3 new since you last read" / "10+ new since you last read". */
export function formatNewSince(n: number): string {
  const count = n >= PREVIEW_PAGE_SIZE ? `${PREVIEW_PAGE_SIZE}+` : String(n)
  return `${count} new since you last read`
}
